/**
 * PROOFED — Subscription Toggle web component
 *
 * Manages:
 *  1. Card selection state (subscribe / onetime)
 *  2. selling_plan input in the nearest product form
 *  3. Price display updates when selection or variant changes
 *  4. Frequency dropdown → plan-id sync
 *
 * Listens for:
 *  - 'variant:change' on document (dispatched by main-product.liquid)
 *    payload: { detail: { variant } }
 *
 * Dispatches:
 *  - 'subscription:change' on self, bubbles
 *    payload: { detail: { type: 'subscribe'|'onetime', planId, price } }
 */

class SubscriptionToggle extends HTMLElement {

  connectedCallback() {
    this._data       = this._parseData();
    this._cards      = [...this.querySelectorAll('[data-option]')];
    this._freqSelect = this.querySelector('[data-frequency-select]');
    this._form       = this._findForm();

    if (!this._data || !this._cards.length) return;

    const defaultType = this.dataset.defaultSelection || 'subscribe';
    this._select(defaultType, false);

    this._cards.forEach(card => {
      card.addEventListener('click', () => this._select(card.dataset.option));
      card.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._select(card.dataset.option);
        }
        // Arrow key navigation within radiogroup
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          this._focusNext(card, 1);
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          this._focusNext(card, -1);
        }
      });
    });

    if (this._freqSelect) {
      this._freqSelect.addEventListener('change', () => {
        if (this._currentType === 'subscribe') {
          this._applySellingPlan(this._freqSelect.value);
        }
      });
    }

    // Recompute prices when variant changes (e.g. if product has variants)
    document.addEventListener('variant:change', this._onVariantChange.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this._onVariantChange.bind(this));
  }

  // ── Selection ──────────────────────────────────────────────

  _select(type, dispatch = true) {
    this._currentType = type;

    this._cards.forEach(card => {
      const isSelected = card.dataset.option === type;
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-checked', String(isSelected));
      card.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    if (type === 'subscribe') {
      const planId = this._freqSelect?.value || String(this._data.defaultPlanId);
      this._applySellingPlan(planId);
    } else {
      this._removeSellingPlan();
    }

    this._refreshPriceDisplay();

    if (dispatch) {
      this.dispatchEvent(new CustomEvent('subscription:change', {
        bubbles: true,
        detail: {
          type,
          planId: type === 'subscribe'
            ? (this._freqSelect?.value || String(this._data.defaultPlanId))
            : null,
          price: this._currentPrice(),
        },
      }));
    }
  }

  _focusNext(currentCard, direction) {
    const idx    = this._cards.indexOf(currentCard);
    const next   = this._cards[(idx + direction + this._cards.length) % this._cards.length];
    next?.focus();
    this._select(next.dataset.option);
  }

  // ── Selling plan form wiring ───────────────────────────────

  _applySellingPlan(planId) {
    if (!this._form) return;
    let input = this._form.querySelector('[name="selling_plan"]');
    if (!input) {
      input      = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      this._form.appendChild(input);
    }
    input.value = planId;
  }

  _removeSellingPlan() {
    this._form?.querySelector('[name="selling_plan"]')?.remove();
  }

  // ── Price display ──────────────────────────────────────────

  _refreshPriceDisplay() {
    const type      = this._currentType;
    const variantId = this._currentVariantId();
    const varData   = this._data.variants[variantId] || this._firstVariantData();
    const basePrice = varData.price;

    let displayPrice;

    if (type === 'subscribe') {
      const planId  = this._freqSelect?.value || String(this._data.defaultPlanId);
      const plan    = this._findPlan(planId);
      displayPrice  = plan ? this._applyAdjustment(basePrice, plan.adjustment) : basePrice;
    } else {
      displayPrice = basePrice;
    }

    // Update price spans
    const subscribePriceEl  = this.querySelector('[data-subscribe-price]');
    const onetimePriceEl    = this.querySelector('[data-onetime-price]');
    const compareEl         = this.querySelector('[data-subscribe-compare]');

    if (subscribePriceEl) subscribePriceEl.textContent = this._formatMoney(displayPrice);
    if (onetimePriceEl)   onetimePriceEl.textContent   = this._formatMoney(basePrice);
    if (compareEl)        compareEl.textContent         = this._formatMoney(basePrice);

    // Update per-day amount spans (both pill and inline variants)
    const days = Number(this.dataset.days) || 30;
    this.querySelectorAll('.price-per-day__amount').forEach((el, i) => {
      const price = i === 0 ? displayPrice : basePrice;
      el.textContent = this._formatMoney(Math.floor(price / days));
    });
  }

  _currentPrice() {
    const variantId = this._currentVariantId();
    const varData   = this._data.variants[variantId] || this._firstVariantData();
    const basePrice = varData.price;
    if (this._currentType !== 'subscribe') return basePrice;
    const planId = this._freqSelect?.value || String(this._data.defaultPlanId);
    const plan   = this._findPlan(planId);
    return plan ? this._applyAdjustment(basePrice, plan.adjustment) : basePrice;
  }

  // ── Variant change handler ─────────────────────────────────

  _onVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant) return;
    this._activeVariantId = String(variant.id);
    this._refreshPriceDisplay();
  }

  // ── Helpers ────────────────────────────────────────────────

  _applyAdjustment(basePrice, adj) {
    if (!adj) return basePrice;
    switch (adj.type) {
      case 'percentage':
        return Math.floor(basePrice * (1 - adj.value / 100));
      case 'fixed_amount':
        return Math.max(0, basePrice - adj.value);
      case 'price':
        return adj.value;
      default:
        return basePrice;
    }
  }

  _findPlan(planId) {
    for (const group of this._data.sellingPlanGroups) {
      const found = group.sellingPlans.find(p => String(p.id) === String(planId));
      if (found) return found;
    }
    return null;
  }

  _currentVariantId() {
    if (this._activeVariantId) return this._activeVariantId;
    // Try to read from Dawn's variant selector
    const input = this._form?.querySelector('[name="id"]');
    return input ? String(input.value) : this._firstVariantId();
  }

  _firstVariantId() {
    return Object.keys(this._data.variants)[0] || '';
  }

  _firstVariantData() {
    return Object.values(this._data.variants)[0] || { price: 0 };
  }

  _parseData() {
    try {
      const script = this.querySelector('[data-subscription-json]');
      return script ? JSON.parse(script.textContent) : null;
    } catch (e) {
      console.warn('[subscription-toggle] Failed to parse subscription data', e);
      return null;
    }
  }

  _findForm() {
    // Walk up the DOM to find the nearest product form
    let el = this;
    while (el && el !== document.body) {
      const form = el.querySelector('form[action="/cart/add"]')
        || el.closest('form[action="/cart/add"]');
      if (form) return form;
      el = el.parentElement;
    }
    return document.querySelector('form[action="/cart/add"]');
  }

  _formatMoney(paise) {
    // Matches Shopify's money_without_trailing_zeros for INR
    const rupees = paise / 100;
    return '₹' + new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: rupees % 1 === 0 ? 0 : 2,
    }).format(rupees);
  }
}

if (!customElements.get('subscription-toggle')) {
  customElements.define('subscription-toggle', SubscriptionToggle);
}
